import React, { useEffect } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import styled from '@emotion/styled';
import {
    Counter, 
    PrimaryButton, 
    RadioButton,
    SpecialButton,
    TextInput,
    Popover,
    InfoIcon
} from '@liveoptics/react-ui';
import { useRef, useState } from 'react';
import Region from './Region';
import useResizeTable from './useResizeTable';
import '../css/helper-styles.css';
import '../css/calc-custom.scss';
import VmConfigurationTable from '../components/VmConfigurationTable';
import GenericModal from '../modals/GenericModal';
import CreateNewSessionModal from '../modals/CreateNewSessionModal';
import VmPricingComparisonChart from '../pricing/VmPricingComparisonChart';
import VmPricingApiService from '../pricing/VmPricingApiService';
import osLinuxIcon from '../svgs/OS-Linux-icon.svg';
import osWindowsIcon from '../svgs/OS-Windows-icon.svg';
import excelIcon from '../svgs/file-excel.svg';
import chartIcon from '../svgs/chart-bar.svg';
import questionCircle from '../svgs/question-circle.svg';
import spinningIcon from '../svgs/spinning-loading-circle.svg';
import DisabledTextButton from '../buttons/DisabledTextButton';
import EnabledTextButton from '../buttons/EnabledTextButton';
import RegionChangeConfirmationModal from '../modals/RegionChangeConfirmationModal';
import { v4 as uuid } from 'uuid';

let RECAPTCHA_SITE_KEY = "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI";

if (process.env.NODE_ENV !== 'development') {
    // Determine if we're on DEV, UAT or PROD and assign non-secret site key accordingly.
    var currentUrl = window.location.hostname;
    console.log(currentUrl);
    if(currentUrl) {
        if (currentUrl.indexOf("liveoptics-dev.dell.com") > -1 || currentUrl.indexOf("liveoptics-sit.dell.com") > -1) { 
            RECAPTCHA_SITE_KEY = "6Ldmw3sbAAAAAA1siVIQNlL7K_ueLa8Kr9FpQ3mq";
        } else if (currentUrl.indexOf("liveoptics.com") > -1) {
            RECAPTCHA_SITE_KEY = "6Lcrx3sbAAAAAA99FkFXb20netTUcBcVuYhfPQVr";
        }
    } else {
        RECAPTCHA_SITE_KEY = "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI";
    }
}

const InputFieldLabelWithTooltipWrapper = styled.div`
    display: flex;
    -webkit-align-items: center;
    -webkit-box-align: center;
    -ms-flex-align: center;
    align-items: center;
    margin-bottom: 5px;
`;

const InputFieldLabel = styled.label`
    color: #566072;
    font-size: 14px;
    display: inline-block;
    margin-right: 10px;
`;

const VMTableWrap = styled.div`
    
`;

const RadioWrapInner = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 6px 36px 8px 0px;
`;

const ButtonWrap = styled.div`
    border-top: 3pt solid #eeeeee;
    padding-top: 25px;
    display: flex;
    justify-content: flex-end;
    align-items: baseline;

    @media (max-width: 1080px) {
        // padding-top: 0;
    }
`;

const ViewerTitle = styled.p`

`;

const RegionSectionWrap = styled.div`
    background: #ffffff;
    min-height: 5rem;
    padding: 20px;
    margin-bottom: 20px;

    @media (max-width: 768px) {
        padding: 15px;
    }
`;

const ActionButtonsWrap = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;

    @media (max-width: 500px) {
        display: block;
    }
`;

const ViewerTitleWrap = styled.div`
    display: flex;
    align-items: baseline;
`;

const regions = [
    {
        key: 'HongKong',
        title: 'Asia Pacific (Hong Kong)',
    },{
        key: 'Mumbai',
        title: 'Asia Pacific (Mumbai)',
    },{
        key: 'Seoul',
        title: 'Asia Pacific (Seoul)',
    },{
        key: 'Singapore',
        title: 'Asia Pacific (Singapore)',
    },{
        key: 'Tokyo',
        title: 'Asia Pacific (Tokyo)',
    },{
        key: 'Australia',
        title: 'Australia',
    },{
        key: 'Canada',
        title: 'Canada',
    },{
        key: 'EUFrankfurt',
        title: 'Europe (Frankfurt)',
    },{
        key: 'EULondon',
        title: 'Europe (London)',
    },{
        key: 'SouthAmerica',
        title: 'South America (Sao Paulo)',
    },{
        key: 'USEast',
        title: 'US East (Virginia)',
    },{
        key: 'USWest',
        title: 'US West (California)',
    }
];

const VMConfiguration = () => {
    const [showModal, setShowModal] = useState<boolean>();
    const [showRegionChangeConfirmation, setShowRegionChangeConfirmation] = useState<boolean>();
    const [showCreateNewSessionModal, setShowCreateNewSessionModal] = useState<boolean>();
    const [currentNumberOfVms, setNumberOfVms] = useState<number>(0);
    const [enableGetPricingComparisonButton, setEnableGetPricingComparisonButton] = useState<boolean>();
    const [isLoadingPricingComparison, setIsLoadingPricingComparison] = useState<boolean>(false);
    const [nextRegion, setNextRegion] = useState<string>();

    const maxNumberOfAllowedVms = 20000;

    const [tableWidth, setTableWidth] = useState(0);
    const {width, height} = useResizeTable('table-container');

    useEffect(() => {
        setTableWidth(width);
    }, [width, height]);

    const initFormState = {
        id: uuid(),
        vmName: '',
        osType: '',
        numberOfVMs: null,
        vCPUPerVM: null,
        memoryPerVM: null,
        storagePerVM: null
    }

    const initGenericModalInfoState = {
        info: {
            title: '',
            text: '',
            css: ''
        }
    };

    const initState = {
        selectedRegion: '',
        pricingComparison: null,
        configuration: {
            title: 'US East',
            formState: initFormState,
            tableData: []            
        }
    };

    const [viewerState, setViewerState] = useState<any>(initState);
    const [genericModalInfoState, setGenericModalInfoState] = useState<any>(initGenericModalInfoState);

    const currentFormState = viewerState.configuration.formState;
    const tableData = viewerState.configuration.tableData;

    const recpatchaRef = useRef<ReCAPTCHA>(null);
    
    function doRowAdd() {
        var numberOfVmsToBeAdded = viewerState.configuration.formState['numberOfVMs'];
        var potentialTotalVms = numberOfVmsToBeAdded + currentNumberOfVms;

        if(currentNumberOfVms > maxNumberOfAllowedVms || potentialTotalVms > maxNumberOfAllowedVms) {
            setGenericModalInfoState({
                info: {
                    title: 'Exceeding VMs Limit',
                    text: 'You are exceeding the maximum number of supported VMs per assessment which is 20,000',
                    css: 'greenbutton'
                }
            });
            setShowModal(true);
        } else {
            setViewerState({
                ...viewerState,
                configuration: {
                    ...viewerState.configuration,
                    tableData: [
                        ...viewerState.configuration.tableData,
                        {
                            ...viewerState.configuration.formState
                        }
                    ]
                }
            })
            
            // After adding a new row to the table, update the state's currentNumberOfVms with the numberOfVmsToBeAdded amount.
            // Also enable the Get Pricing Comparison button.
            viewerState.configuration.formState['id'] = uuid();
            setNumberOfVms(currentNumberOfVms + numberOfVmsToBeAdded);
            setEnableGetPricingComparisonButton(true);
        }
    }

    async function getPricingComparison() {
        setIsLoadingPricingComparison(true);

        var recaptchaToken = await recpatchaRef.current.executeAsync();
        recpatchaRef.current.reset();
        var response = await VmPricingApiService.getPricingComparison(recaptchaToken, viewerState.selectedRegion, tableData);
        if(response.status !== 200) {
            console.log('error: ' + JSON.stringify(response));

            if(response.status === 403) {
                setGenericModalInfoState({
                    info: {
                        title: 'Your session has expired',
                        text: 'We could not process your request because your session has expired. Please refresh the page to start a new one. A reminder that a single session can only last up to 48 hours. We are sorry for the inconvenience.',
                        css: 'greenbutton'
                    }
                });
                setShowModal(true);
            } else {
                setGenericModalInfoState({
                    info: {
                        title: 'An error has occured',
                        text: 'An error occured trying load the pricing comparison. Please refresh the page and try again. We are sorry for the inconvenience.',
                        css: 'greenbutton'
                    }
                });
                setShowModal(true);
            }
        } else {
            const json = await response.json();
            setViewerState({
                ...viewerState,
                pricingComparison: json.data
            });
            setEnableGetPricingComparisonButton(false);
            setIsLoadingPricingComparison(false);
        }
    }

    async function getExcel() {
        var recaptchaToken = await recpatchaRef.current.executeAsync();
        recpatchaRef.current.reset();
        var response = await VmPricingApiService.getExcel(recaptchaToken, viewerState.selectedRegion, tableData);
        if(!response || !response.blob || response.status !== 200) {
            console.log('error: ' + JSON.stringify(response));

            if(response.status === 403) {
                setGenericModalInfoState({
                    info: {
                        title: 'Your session has expired',
                        text: 'We could not process your request because your session has expired. Please refresh the page to start a new one. A reminder that a single session can only last up to 48 hours. We are sorry for the inconvenience.',
                        css: 'greenbutton'
                    }
                });
                setShowModal(true);
            } else {
                setGenericModalInfoState({
                    info: {
                        title: 'An error has occured',
                        text: 'An error occured trying get the Excel Estimates report. Please refresh the page and try again. We are sorry for the inconvenience.',
                        css: 'greenbutton'
                    }
                });
                setShowModal(true);
            }
        } else {
            let d = new Date();
            let dateTime = d.toLocaleString();
            const blob = await response.blob();
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'CloudPricing Estimates-Data-' + dateTime + '.xlsx');
            document.body.appendChild(link);
            link.click();
        }
    }

    const isVMConfigurationPresent = () => {
        if(!viewerState.selectedRegion) {
            return false;
        }

        for (let key in currentFormState) {
            if(key !== 'vmName' && !currentFormState[key]) {
                return false;
            }
        }
        return true;
    }

    const isAtLeastOneConfigurationInFormPresent = () => {
        if(viewerState.selectedRegion) {
            return true;
        } else {
            for (let key in currentFormState) {
                if(currentFormState[key]) {
                    return true;
                }
            }
        }

        return false;
    }

    const resetFormState = () => {
        setViewerState({
            ...viewerState,
            configuration: {
                ...viewerState.configuration,
                formState: {
                    ...currentFormState,
                    ...initFormState
                }
            }
        })
    }

    // If the user wants to create a new session i.e. delete all the VMs they have in the table, simply refresh the page.
    const createNewSession = () => {
        window.location.reload();
    }

    const onRegionChange = (region:any) => {
        setViewerState({
            ...viewerState,
            selectedRegion: region,
            pricingComparison: null    
        });

        setEnableGetPricingComparisonButton(true);
    }

    const onValueChange = ({key, value}:any) => {
        setViewerState({
            ...viewerState,
            configuration: {
                ...viewerState.configuration,
                formState: {
                    ...viewerState.configuration.formState,
                    [key]: value
                }
            }
        });
    }

    return (
        <>
            <ViewerTitleWrap>
                <ViewerTitle className='viewer-title'>Cloud Pricing Calculator</ViewerTitle>
                <div>
                    <a href="https://www.dell.com/support/search/#q=Live%20Optics&sort=relevancy" target="_blank" className="support-link" rel="noopener noreferrer">Support</a> <img className='os-icons' src={questionCircle} alt="" />
                </div>
            </ViewerTitleWrap>
            <RegionSectionWrap>
                <div className='section-title region'>Region</div>
                <Region selectedRegion={viewerState.selectedRegion} onRegionSelect={(val) => {onRegionChange(val)}} regions={regions} />
            </RegionSectionWrap>

            <div className="calc-form-wrap">
                <div className="section-title">Specify the VM Configuration</div>
                <div className="calc-form-inner">
                    <div className='row'>
                        <div className="calc-form-field">
                            <InputFieldLabelWithTooltipWrapper>
                                <InputFieldLabel>Name (optional)</InputFieldLabel>
                                <Popover text="Enter the VM or VMs Group name, this field is optional ">
                                    <InfoIcon />
                                </Popover>
                            </InputFieldLabelWithTooltipWrapper>
                            <TextInput onChange={(val)=>{onValueChange({key: 'vmName', value: val.currentTarget.value})}} sizing="small" value={String(currentFormState.vmName)} />
                        </div>
                        <div className="calc-form-field radio-group">
                            <InputFieldLabelWithTooltipWrapper>
                                <InputFieldLabel>OS Type</InputFieldLabel>
                                <Popover text="Choose the operating system running on the VM, license prices will be based on the basic version of Linux and Windows server based on your choice.">
                                    <InfoIcon />
                                </Popover>
                            </InputFieldLabelWithTooltipWrapper>
                            <RadioWrapInner>
                                <div className="radio-button">
                                    <RadioButton checked={currentFormState.osType === 'Linux'} onChange={() =>{onValueChange({key: 'osType', value: 'Linux'})}} id="linux" type="radio" name="os-types" value="Linux" />
                                    <label htmlFor="linux"><img className='os-icons' src={osLinuxIcon} /> Linux</label>
                                </div>
                                <div className="radio-button">
                                    <RadioButton checked={currentFormState.osType === 'Windows'} onChange={() =>{onValueChange({key: 'osType', value: 'Windows'})}} id="windows" type="radio" name="os-types" value="Windows" />
                                    <label htmlFor="windows"><img className='os-icons' src={osWindowsIcon} /> Windows</label>                    
                                </div>
                            </RadioWrapInner>
                        </div>
                        <div className="calc-form-field input-text-counter">
                            <InputFieldLabelWithTooltipWrapper>
                                <InputFieldLabel>Number of VMs</InputFieldLabel>
                                <Popover text="The number of VMs per configuration. The total number of VMs supported for the full assessment is 20,000.">
                                    <InfoIcon />
                                </Popover>
                            </InputFieldLabelWithTooltipWrapper>
                            <Counter onCountChange={(val)=>{onValueChange({key: 'numberOfVMs', value: val})}} minVal={0} maxVal={20000} value={Number(currentFormState.numberOfVMs)} />
                        </div>
                        <div className="calc-form-field input-text-counter">
                            <InputFieldLabelWithTooltipWrapper>
                                <InputFieldLabel>vCPU/VM</InputFieldLabel>
                                <Popover text="The number of vCPUs per VM.">
                                    <InfoIcon />
                                </Popover>
                            </InputFieldLabelWithTooltipWrapper>
                            <Counter onCountChange={(val)=>{onValueChange({key: 'vCPUPerVM', value: val})}} minVal={0} maxVal={9999} value={Number(currentFormState.vCPUPerVM)} />
                        </div>
                        <div className="calc-form-field input-text-counter">
                            <InputFieldLabelWithTooltipWrapper>
                                <InputFieldLabel>Memory (GiB)/VM</InputFieldLabel>
                                <Popover text="The memory size in GiB per VM.">
                                    <InfoIcon />
                                </Popover>
                            </InputFieldLabelWithTooltipWrapper>
                            <Counter onCountChange={(val)=>{onValueChange({key: 'memoryPerVM', value: val})}} minVal={0} maxVal={999999} value={Number(currentFormState.memoryPerVM)} />
                        </div> 
                        <div className="calc-form-field input-text-counter">
                            <InputFieldLabelWithTooltipWrapper>
                                <InputFieldLabel>Storage (GiB)/VM</InputFieldLabel>
                                <Popover text="The attached block storage size in GiB per VM.">
                                    <InfoIcon />
                                </Popover>
                            </InputFieldLabelWithTooltipWrapper>
                            <Counter onCountChange={(val)=>{onValueChange({key: 'storagePerVM', value: val})}} minVal={0} maxVal={999999} value={Number(currentFormState.storagePerVM)} />
                        </div> 
                    </div>
                
                    <ButtonWrap>
                        {
                            isAtLeastOneConfigurationInFormPresent() ? 
                                <div style={{marginRight: '1rem'}}><EnabledTextButton clickFn={()=>{resetFormState()}} className="blue-text-button">Clear</EnabledTextButton></div> 
                                    : <div style={{marginRight: '1rem'}}><DisabledTextButton>Clear</DisabledTextButton></div> 
                        }
                        <PrimaryButton disabled={!isVMConfigurationPresent()} clickFn={doRowAdd}>Add</PrimaryButton>
                    </ButtonWrap>
                    <ReCAPTCHA
                        sitekey={RECAPTCHA_SITE_KEY}
                        size="invisible"
                        ref={recpatchaRef}
                    />
                </div>
                <VMTableWrap className="table-container">
                    {
                        tableData.length > 0 ? <VmConfigurationTable data={tableData} currentNumberOfVms={currentNumberOfVms} setCurrentNumberOfVms={setNumberOfVms} viewerState={viewerState} setViewerState={setViewerState} setEnableGetPricingComparisonButton={setEnableGetPricingComparisonButton} /> : null
                    }
                </VMTableWrap>
            </div>

            <ActionButtonsWrap className="action-buttons-wrapper">
                {
                    tableData.length > 0 ? 
                        <EnabledTextButton clickFn={()=>{setShowCreateNewSessionModal(true)}} className="green-text-button">Create New</EnabledTextButton>
                    : <DisabledTextButton>Create New</DisabledTextButton>
                }
                
                <SpecialButton className="btnGetPricing" disabled={tableData.length === 0 || !enableGetPricingComparisonButton} onClick={() => {getPricingComparison()}}>{isLoadingPricingComparison ? <img className='os-icons' src={spinningIcon} /> : <img className='os-icons' src={chartIcon} />} Get Pricing Comparison</SpecialButton>
                <SpecialButton className="btnGetExcel" disabled={tableData.length === 0} onClick={() => {getExcel()}}><img className='os-icons' src={excelIcon} />Download Excel Estimates</SpecialButton>
            </ActionButtonsWrap>

            {
                isLoadingPricingComparison || viewerState.pricingComparison ?
                    <div className='calc-form-wrap'>
                        <VmPricingComparisonChart data={viewerState.pricingComparison} reCaptchaRef={recpatchaRef} isLoadingPricingComparison={isLoadingPricingComparison} />
                    </div> : null
            }

            {
                showModal ?
                    <GenericModal 
                        modalTitle={genericModalInfoState.info.title}
                        modalText={genericModalInfoState.info.text}
                        cssClassName={genericModalInfoState.info.css}
                        closeFunction={() => setShowModal(false)}
                    /> : null
            }

            {
                showCreateNewSessionModal ?
                    <CreateNewSessionModal 
                        createNewFunction={() => createNewSession()}
                        closeFunction={() => setShowCreateNewSessionModal(false)}
                    /> : null
            }   

            {
                showRegionChangeConfirmation ? 
                    <RegionChangeConfirmationModal 
                        modalTitle="Confirm you want to change your selected Region"
                        nextRegion={nextRegion}
                        changeRegion={onRegionChange}
                        closeFn={() => setShowRegionChangeConfirmation(false)}
                    /> : null
            }         
        </>
    )
}

export default VMConfiguration;